import { useMemo, useState } from "react";
import { useAuth } from "../../js/hooks/useAuth";
import { api } from "../../js/api/messaging";
import {
  GUIDED_HELP,
  broadcastAnnouncement,
  followUpFor,
  tagThreadFollowUp,
} from "../../js/api/adminMessaging";
import { listFollowUps } from "@hub";

export default function AdminMessageTools({ selected, locked, onSend, onNotice }) {
  const { session } = useAuth();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [followNote, setFollowNote] = useState("");
  const [helpId, setHelpId] = useState(GUIDED_HELP[0].id);
  const [pulse, setPulse] = useState(0);

  const threadId = selected?.threadId;
  const follow = useMemo(() => (threadId ? followUpFor(threadId) : null), [threadId, pulse]);
  const tagged = useMemo(() => listFollowUps(), [pulse]);

  async function sendBroadcast(event) {
    event.preventDefault();
    if (!title.trim() || !body.trim()) {
      onNotice?.("Add a title and a short announcement.");
      return;
    }
    const result = broadcastAnnouncement({
      title: title.trim(),
      body: body.trim(),
      from_name: session?.name || "Site admin",
      actor_id: session?.userId,
    });
    setTitle("");
    setBody("");
    onNotice?.(`Announcement posted to Notices and sent to ${result.recipients} accounts.`);
  }

  function toggleFollow() {
    if (!threadId) {
      onNotice?.("Open a thread first.");
      return;
    }
    tagThreadFollowUp(threadId, {
      tagged: !follow,
      note: followNote,
      tagged_by: session?.name || "admin",
    });
    setPulse((n) => n + 1);
    onNotice?.(follow ? "Follow-up tag cleared." : "Thread tagged for follow-up.");
  }

  async function sendGuided() {
    const note = GUIDED_HELP.find((item) => item.id === helpId);
    if (!threadId || locked) {
      onNotice?.("Open an active thread to send guided help.");
      return;
    }
    if (onSend) {
      await onSend(note.text, []);
      onNotice?.("Guided help was added to the thread.");
      return;
    }
    const result = await api(
      "POST",
      "/api/messages/send",
      { thread_id: threadId, message_text: note.text },
      session
    );
    onNotice?.(result.ok ? "Guided help was added to the thread." : result.error);
  }

  return (
    <section className="desk-panel card admin-tools">
      <h2>Admin tools</h2>
      <p className="muted">
        Same inbox as everyone else. These extras sit on top — they do not open a second mailbox.
      </p>
      <div className="admin-tools__grid">
        <form onSubmit={sendBroadcast}>
          <h3>Broadcast</h3>
          <label className="field">
            <span className="field-label">Title</span>
            <input className="field-input" value={title} onChange={(event) => setTitle(event.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Announcement</span>
            <textarea className="field-textarea" rows={3} value={body} onChange={(event) => setBody(event.target.value)} />
          </label>
          <button type="submit" className="primary-btn">
            Send to all accounts
          </button>
        </form>

        <div>
          <h3>Follow-up</h3>
          <p className="muted">
            {threadId
              ? follow
                ? `Tagged ${follow.tagged_at ? "" : ""}for follow-up.`
                : "This thread is not tagged."
              : "Select a thread to tag it."}
          </p>
          <label className="field">
            <span className="field-label">Private note</span>
            <input
              className="field-input"
              value={followNote}
              onChange={(event) => setFollowNote(event.target.value)}
              placeholder="Why this needs a look later"
            />
          </label>
          <button type="button" className="ghost-btn" onClick={toggleFollow} disabled={!threadId}>
            {follow ? "Clear follow-up" : "Tag for follow-up"}
          </button>
          {tagged.length ? (
            <p className="muted" style={{ marginTop: "0.65rem" }}>
              {tagged.length} thread{tagged.length === 1 ? "" : "s"} tagged on this device.
            </p>
          ) : null}
        </div>

        <div>
          <h3>Guided help</h3>
          <p className="muted">A canned note into the open thread. Attach a link or document in the composer below.</p>
          <label className="field">
            <span className="field-label">Note</span>
            <select className="field-select" value={helpId} onChange={(event) => setHelpId(event.target.value)}>
              {GUIDED_HELP.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="primary-btn" onClick={sendGuided} disabled={!threadId || locked}>
            Add to thread
          </button>
        </div>
      </div>
    </section>
  );
}
